import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
  Pagination,
  Backdrop,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  Stack,
  Typography,
} from '@mui/material';

import HttpService from '../../hooks/Https-services';
import PAGE_ROUTES, {
  API_ROUTES,
  hasRole,
  PRIORITY,
  ROLE_IDS,
  dateFormat,
  timeFormat,
} from '../../utils/constant';
import { useNavigate } from 'react-router-dom';

import { visuallyHidden } from '@mui/utils';
import moment from 'moment';

import useCommonDetails from '../../hooks/useCommonDetails';
import INQCTableRow from './inQCTableRow';

export default function InQCTable() {
  const [rows, setRows] = useState<any>([]);
  const [orderBy, setOrderBy] = React.useState<any>(null); // default sort by 'priority'
  const [order, setOrder] = React.useState<any>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(10);
  const itemsPerPage = 100;
  const navigate = useNavigate();
  const currentUserInfo = useCommonDetails();
  const [loading, setLoading] = React.useState(false);
  const [teams, setTeams] = React.useState<any>([]);
  const [selectedTeams, setSelectedTeams] = React.useState<any>([]);
  const [selectedTeamsKeys, setSelectedTeamsKeys] = React.useState<any[]>([]);
  const [isTeamLoaded, setIsTeamLoaded] = React.useState(false);
  const [selectPriority, setSelectPriority] = React.useState<any[]>([]);
  const [selectedPriorityKeys, setSelectedPriorityKeys] = useState<number[]>(
    []
  );

  const handleChangePriority = (event: any, newValue: any) => {
    setCurrentPage(1);
    setSelectPriority(newValue);
    const selectedKeys = newValue.map((option: { key: number }) => option.key);
    setSelectedPriorityKeys(selectedKeys);
  };

  const priorityOptions = Object.entries(PRIORITY).map(([key, value]) => ({
    key: Number(key), // Convert key to a number
    label: value,
  }));

  const fetchTeamsList = React.useCallback(async () => {
    try {
      const response = await HttpService.get(API_ROUTES.fatchAllTeam);
      const teamsData = response.data.data;
      setTeams(teamsData);

      // Select all teams except the one with id 1
      const defaultSelectedTeams = teamsData.filter(
        (team: any) => team.id !== 1
      );
      setIsTeamLoaded(true);
      setSelectedTeams(defaultSelectedTeams);
      setSelectedTeamsKeys(defaultSelectedTeams.map((team: any) => team.id));
      setSelectPriority([
        {
          key: 1,
          label: 'NORMAL',
        },
        { key: 2, label: 'MAJOR' },
        { key: 3, label: 'CRITICAL' },
        { key: 4, label: 'SHOW_STOPPER' },
      ]);
      setSelectedPriorityKeys([1, 2, 3, 4]);
    } catch (error: any) {
      setIsTeamLoaded(true);
      console.error('Error fetching teams list:', error);
    }
  }, []);

  // Call fetchTeamsList only once on component mount
  React.useEffect(() => {
    fetchTeamsList();
  }, [fetchTeamsList]);
  const handleChangeTeams = (event: any, newValue: any) => {
    setCurrentPage(1);
    setSelectedTeams(newValue);
    const selectedKeys = newValue.map((option: any) => option.id);
    setSelectedTeamsKeys(selectedKeys);
  };
  const clearFilter = () => {
    setSelectedTeamsKeys([]);
    setSelectedTeams([]);
    setIsTeamLoaded(true);
    setCurrentPage(1);
    setSelectPriority([]);
    setSelectedPriorityKeys([]);
  };
  const fetchTableData = React.useCallback(async () => {
    try {
      setLoading(true);
      const query: any = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (orderBy && order) {
        (query.sortBy = orderBy), (query.orderBy = order);
      }
      if (!hasRole(currentUserInfo.roleId, [ROLE_IDS.MODERATOR])) {
        if (selectedTeamsKeys.length > 0) {
          query.teamId = selectedTeamsKeys.join(',');
        }
      }
      if (selectedPriorityKeys.length > 0) {
        query.priority = selectedPriorityKeys.join(',');
      }
      const response = await HttpService.get(
        API_ROUTES.inQC + `?${new URLSearchParams(query).toString()}`
      );
      setRows(response.data.data.list);
      setTotalPages(response.data.data.totalPages);
    } catch (error: any) {
      console.error('Error fetching games list:', error);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    order,
    selectedTeams,
    isTeamLoaded,
    selectedPriorityKeys,
  ]);

  useEffect(() => {
    if (isTeamLoaded) {
      fetchTableData();
      return () => {
        setRows([]);
      };
    }
  }, [fetchTableData]);

  const handleSort = (property: any) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page); // Update the current page
  };

  const handleCardClick = (searchGameId: string) => {
    // Navigate to the games page and pass the status as state
    navigate(PAGE_ROUTES.GAMES, { state: { searchGameId } });
  };

  return (
    <>
      {!hasRole(currentUserInfo.roleId, [ROLE_IDS.MODERATOR]) && (
        <Box
          sx={{
            display: 'flex',
            padding: '20px 0',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Autocomplete
              size="small"
              multiple
              sx={{ minWidth: 200 }}
              id="role-selector"
              options={teams}
              value={selectedTeams} // Replace with your selected roles state
              getOptionLabel={(option: any) => option.userTeamKey}
              onChange={(event, newValue) => {
                handleChangeTeams(event, newValue); // Replace with your role change handler
              }}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Teams" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <Autocomplete
              size="small"
              multiple
              fullWidth
              id="tags-outlined"
              options={priorityOptions}
              value={selectPriority}
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => {
                handleChangePriority(event, newValue);
              }}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Priority" />
              )}
              isOptionEqualToValue={(option, value) => option.key === value.key}
            />

            <Box>
              <Button
                onClick={() => clearFilter()}
                variant="contained"
                sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
              >
                Clear Filter
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table
          sx={{
            '& .MuiTableCell-sizeSmall': {
              padding: '0px 0px 0px 0px !important',
            },
          }}
        >
          <TableHead sx={{ padding: '0px !important' }}>
            <TableRow sx={{ padding: '0px !important' }}>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'gameId'}
                  direction={orderBy === 'gameId' ? order : 'asc'}
                  onClick={handleSort('gameId')}
                >
                  GameId
                  {orderBy === 'gameId' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'priority'}
                  direction={orderBy === 'priority' ? order : 'asc'}
                  onClick={handleSort('priority')}
                >
                  Priority
                  {orderBy === 'priority' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left" sx={{ minWidth: 190 }}>
                <TableSortLabel
                  active={orderBy === 'gameDate'}
                  direction={orderBy === 'gameDate' ? order : 'asc'}
                  onClick={handleSort('gameDate')}
                >
                  Date
                  {orderBy === 'gameDate' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                Teams
                {/* <TableSortLabel
                  // active={orderBy === 'assignedAt'}
                  // direction={orderBy === 'assignedAt' ? order : 'asc'}
                  // onClick={handleSort('assignedAt')}
                >
                  
                   {orderBy === 'assignedAt' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null} *
                </TableSortLabel> */}
              </TableCell>
              <TableCell align="left" sx={{ minWidth: 190 }}>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={handleSort('createdAt')}
                >
                  Fetched
                  {orderBy === 'createdAt' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'userTeamKey'}
                  direction={orderBy === 'userTeamKey' ? order : 'asc'}
                  onClick={handleSort('userTeamKey')}
                >
                  Team
                  {orderBy === 'gameId' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'moderatorName'}
                  direction={orderBy === 'moderatorName' ? order : 'asc'}
                  onClick={handleSort('moderatorName')}
                >
                  Moderator
                  {orderBy === 'gameId' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'qcModeratorName'}
                  direction={orderBy === 'qcModeratorName' ? order : 'asc'}
                  onClick={handleSort('qcModeratorName')}
                >
                  QC Moderator
                  {orderBy === 'gameId' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'assignedAt'}
                  direction={orderBy === 'assignedAt' ? order : 'asc'}
                  onClick={handleSort('assignedAt')}
                >
                  Elapsed
                  {orderBy === 'assignedAt' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any, index: number) => (
              <TableRow
                key={index}
                onClick={() => handleCardClick(row?.gameId)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{row?.gameId ? row?.gameId : '-'}</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      background:
                        {
                          MINOR: '#eeeeee',
                          NORMAL: '#dbf2ff',
                          MAJOR: '#eb6f33',
                          CRITICAL: '#d30584',
                          SHOW_STOPPER: '#e30000',
                        }[PRIORITY[row.priority]] || '#ffffff', // Default to white if no match
                      color: '#000',
                      height: '40px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '0 10px',
                      borderRadius: 3,
                      fontWeight: 600,
                    }}
                  >
                    {PRIORITY?.[row.priority]}
                  </Box>
                </TableCell>

                {/* <TableCell align="left">{PRIORITY?.[row.priority]}</TableCell> */}
                <TableCell align="left">
                  {row?.game?.dateTime ? (
                    <Stack direction={'column'}>
                      <Typography variant="body2">
                        {moment(row?.game?.dateTime).format(`${dateFormat}`)}
                      </Typography>
                      <Typography variant="body2">
                        {moment(row?.game?.dateTime).format(`${timeFormat}`)}
                      </Typography>
                    </Stack>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="left">
                  {row?.game?.awayTeam?.nameEn} vs {row?.game?.homeTeam?.nameEn}
                </TableCell>
                <TableCell align="left">
                  {row?.createdAt ? (
                    <Stack direction={'column'}>
                      <Typography variant="body2">
                        {moment(row?.createdAt).format(`${dateFormat}`)}
                      </Typography>
                      <Typography variant="body2">
                        {moment(row?.createdAt).format(`${timeFormat}`)}
                      </Typography>
                    </Stack>
                  ) : (
                    '-'
                  )}
                </TableCell>

                <TableCell align="left">
                  {row?.userTeamKey ? row?.userTeamKey : '-'}
                </TableCell>
                <TableCell align="left">
                  {row?.moderatorName ? row?.moderatorName : '-'}
                </TableCell>
                <TableCell align="left">
                  {row?.qcModeratorName ? row?.qcModeratorName : '-'}
                </TableCell>
                <TableCell align="left">
                  {/* {formatElapsedTime(row?.assignedAt)} */}
                  <INQCTableRow row={row} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination Component */}
      <Box display="flex" justifyContent="center" my={2}>
        <Pagination
          count={totalPages} // Total number of pages
          page={currentPage} // Current page
          onChange={handlePageChange} // Handle page change
          color="primary"
        />
      </Box>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
